/**
 * <hero-slideshow> — self-contained hero carousel.
 *
 * Deliberately does NOT reuse the theme's SlideshowComponent (assets/global.js:752),
 * and shares no class names with it.
 *
 * Why: component-slideshow.css declares bare rules on `slideshow-component` and
 * `.slideshow__slide` — both `display: flex; flex-direction: column` — which fight this
 * section's layout. That stylesheet loads whenever any other slideshow section sits on
 * the page, so reusing those names would mean inheriting the rules with no way to opt
 * out, and overriding them with !important.
 *
 * Behaviour hooks are data-attributes, never classes, so restyling this section can
 * never break its behaviour.
 *
 * Layout reads are done in one place — measure() — and cached. Nothing on the scroll,
 * drag or slide-change path touches offsetLeft/offsetHeight, so none of them can force
 * a synchronous reflow.
 */

/** Movement below this is a click that wobbled, not a drag. */
const DRAG_THRESHOLD = 5;

/** Share of the track a mouse drag must cover to change slide rather than settle back. */
const DRAG_ADVANCE_RATIO = 0.2;

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));

    // Nothing to navigate. Leave it as static markup rather than half-initialising.
    if (!this.track || this.slides.length < 2) return;

    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.prevButton = this.querySelector('[data-hero-prev]');
    this.nextButton = this.querySelector('[data-hero-next]');
    this.pauseButton = this.querySelector('[data-hero-pause]');

    // Queried once. setActive runs on every scroll tick that crosses a slide boundary,
    // and re-querying each slide's links there was the most repeated DOM work here.
    this.slideLinks = this.slides.map((slide) =>
      Array.from(slide.querySelectorAll('a[href], a[role="link"]'))
    );

    // Slides become inert when they are off-screen, which drops focus to <body>.
    // Making the group itself focusable gives that focus somewhere labelled to land.
    this.slides.forEach((slide) => {
      slide.tabIndex = -1;
    });

    this.index = 0;
    this.isPlaying = false;
    this.isVisible = true;
    this.autoplayTimer = null;
    this.autoplayDelay = (parseInt(this.dataset.speed, 10) || 5) * 1000;

    // Read live, not once: a visitor can turn the OS setting on while the page is open.
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reduceMotion = this.motionQuery.matches;
    this.handleMotionChange = (event) => {
      this.reduceMotion = event.matches;
    };
    this.motionQuery.addEventListener('change', this.handleMotionChange);

    // Adapt mode only: every slide carries its own height, so the track has to follow
    // the active one. The fixed presets make all slides the same height already.
    this.adaptHeight = this.dataset.adaptHeight === 'true';

    this.handleScroll = this.onFrame(this.syncFromScroll.bind(this));
    this.handleResize = this.onFrame(this.measure.bind(this));
    this.handlePrev = () => this.step(-1);
    this.handleNext = () => this.step(1);
    this.handlePauseToggle = () => (this.isPlaying ? this.stop() : this.start());
    this.handleSuspend = () => this.isPlaying && this.suspend();
    this.handleResume = () =>
      this.isPlaying && this.isVisible && !document.hidden && this.resume();

    // Mouse drag. Touch is left alone on purpose: an overflow container already
    // drag-scrolls under a finger, with momentum and snapping the browser does better
    // than any re-implementation. A mouse gets none of that for free.
    this.isDragging = false;
    this.dragDistance = 0;
    this.suppressClick = false;

    // A high-polling-rate mouse fires several pointermove events per frame, and each
    // scrollLeft write is a layout. Coalescing to one write per frame drops the rest.
    const applyDragScroll = this.onFrame(() => {
      if (!this.isDragging) return;
      this.track.scrollLeft = this.dragStartScroll - this.dragDistance;
    });

    this.handlePointerDown = (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      // Cancels the compatibility mousedown, which is what would otherwise start a
      // text selection or an image ghost-drag halfway through the gesture. Clicks
      // still fire, so links in the slide keep working.
      event.preventDefault();

      this.isDragging = true;
      this.suppressClick = false;
      this.dragDistance = 0;
      this.dragStartX = event.clientX;
      this.dragStartScroll = this.track.scrollLeft;

      // Capture keeps the move and up events coming once the cursor leaves the track.
      this.track.setPointerCapture(event.pointerId);
      this.track.classList.add('hero-slideshow__track--dragging');
    };

    this.handlePointerMove = (event) => {
      if (!this.isDragging) return;
      this.dragDistance = event.clientX - this.dragStartX;
      applyDragScroll();
    };

    this.handlePointerUp = (event) => {
      if (!this.isDragging) return;
      this.isDragging = false;

      if (this.track.hasPointerCapture(event.pointerId)) {
        this.track.releasePointerCapture(event.pointerId);
      }
      this.track.classList.remove('hero-slideshow__track--dragging');

      const travelled = this.dragDistance;
      this.dragDistance = 0;

      // Anything that moved is a drag, not a click — otherwise releasing over a button
      // would follow its link.
      this.suppressClick = Math.abs(travelled) > DRAG_THRESHOLD;

      if (Math.abs(travelled) > this.trackWidth * DRAG_ADVANCE_RATIO) {
        this.step(travelled < 0 ? 1 : -1);
      } else {
        // Snap back: the class that suppressed scroll-snap is off, but the track is
        // still sitting between two slides.
        this.goTo(this.index);
      }
    };

    this.handleClickCapture = (event) => {
      if (!this.suppressClick) return;
      this.suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
    this.track.addEventListener('click', this.handleClickCapture, true);

    // Left/right arrows while focus is inside the carousel. Safe to preventDefault:
    // this section contains no text inputs that would need the keys themselves.
    this.handleKeydown = (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.step(event.key === 'ArrowLeft' ? -1 : 1);
    };
    this.addEventListener('keydown', this.handleKeydown);

    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    // Slide heights are in vw and slide offsets are in px, so a viewport change moves
    // both. Only adapt mode changes height, but the offsets matter either way.
    window.addEventListener('resize', this.handleResize);

    if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrev);
    if (this.nextButton) this.nextButton.addEventListener('click', this.handleNext);

    this.dotHandlers = this.dots.map((dot, i) => {
      const handler = () => this.goTo(i);
      dot.addEventListener('click', handler);
      return handler;
    });

    this.measure();
    this.setActive(0);
    this.initialized = true;

    // A web font swapping in changes text height, and text taller than the image-derived
    // min-height is what decides the slide's height. One re-measure covers it.
    if (document.fonts && document.fonts.status !== 'loaded') {
      document.fonts.ready.then(() => {
        if (this.isConnected) this.measure();
      });
    }

    if (this.dataset.autoplay === 'true') {
      if (this.pauseButton) this.pauseButton.addEventListener('click', this.handlePauseToggle);
      this.addEventListener('mouseenter', this.handleSuspend);
      this.addEventListener('mouseleave', this.handleResume);
      this.addEventListener('focusin', this.handleSuspend);
      this.addEventListener('focusout', this.handleResume);

      // Rotating a hero nobody is looking at costs a smooth-scroll animation every few
      // seconds for nothing. Both of these park the timer until it can be seen again.
      this.observer = new IntersectionObserver((entries) => {
        this.isVisible = entries[0].isIntersecting;
        if (this.isVisible) {
          this.handleResume();
        } else {
          this.handleSuspend();
        }
      });
      this.observer.observe(this);

      this.handleVisibilityChange = () =>
        document.hidden ? this.handleSuspend() : this.handleResume();
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      // Respect prefers-reduced-motion: wire the controls up but do not auto-start.
      // stop() leaves the button showing "play", so a visitor can still opt in.
      if (this.reduceMotion) {
        this.stop();
      } else {
        this.start();
      }
    }
  }

  /**
   * The theme editor tears sections down and rebuilds them on every settings change.
   * Without this, timers and listeners accumulate on each edit.
   */
  disconnectedCallback() {
    // connectedCallback returns early for a single slide, in which case there is
    // nothing wired up to tear down.
    if (!this.initialized) return;

    this.suspend();

    this.removeEventListener('keydown', this.handleKeydown);
    this.track.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.motionQuery.removeEventListener('change', this.handleMotionChange);

    this.track.removeEventListener('pointerdown', this.handlePointerDown);
    this.track.removeEventListener('pointermove', this.handlePointerMove);
    this.track.removeEventListener('pointerup', this.handlePointerUp);
    this.track.removeEventListener('pointercancel', this.handlePointerUp);
    this.track.removeEventListener('click', this.handleClickCapture, true);
    if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrev);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNext);
    if (this.pauseButton) this.pauseButton.removeEventListener('click', this.handlePauseToggle);

    this.removeEventListener('mouseenter', this.handleSuspend);
    this.removeEventListener('mouseleave', this.handleResume);
    this.removeEventListener('focusin', this.handleSuspend);
    this.removeEventListener('focusout', this.handleResume);

    if (this.observer) this.observer.disconnect();
    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    if (this.dotHandlers) {
      this.dots.forEach((dot, i) => dot.removeEventListener('click', this.dotHandlers[i]));
    }
  }

  /**
   * The component's only layout read. Every other path works from these numbers, so
   * scrolling, dragging and changing slide stay write-only.
   *
   * Reads are grouped ahead of the single write in syncTrackHeight: interleaving them
   * is what turns a measurement into a forced reflow per slide.
   */
  measure() {
    this.slideOffsets = this.slides.map((slide) => slide.offsetLeft);
    this.slideHeights = this.slides.map((slide) => slide.offsetHeight);
    this.trackWidth = this.track.clientWidth;

    this.syncTrackHeight();
  }

  step(direction) {
    const next = (this.index + direction + this.slides.length) % this.slides.length;
    this.goTo(next);
  }

  goTo(index) {
    if (index < 0 || index >= this.slides.length) return;

    // Offsets are measured against the track because the stylesheet gives
    // .hero-slideshow__track position: relative. Do not remove that rule.
    this.track.scrollTo({
      left: this.slideOffsets[index],
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });

    this.setActive(index);
  }

  /** Keeps dots and aria state correct when the user swipes or trackpad-scrolls. */
  syncFromScroll() {
    const target = this.track.scrollLeft;
    let closest = 0;
    let smallest = Infinity;

    this.slideOffsets.forEach((offset, i) => {
      const distance = Math.abs(offset - target);
      if (distance < smallest) {
        smallest = distance;
        closest = i;
      }
    });

    if (closest !== this.index) this.setActive(closest);
  }

  setActive(index) {
    const outgoing = this.slides[this.index];
    const heldFocus = index !== this.index && outgoing && outgoing.contains(document.activeElement);

    this.index = index;

    this.slides.forEach((slide, i) => {
      const isActive = i === index;

      // inert covers every focusable an off-screen slide might hold, not just the links
      // below, and keeps the whole subtree out of the accessibility tree.
      slide.inert = !isActive;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      // Tab-order fallback for browsers that do not support inert.
      this.slideLinks[i].forEach((link) => {
        if (isActive) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    });

    // Going inert under the caret drops focus on <body>, which leaves a keyboard user
    // with no idea where they are. The incoming slide is a labelled group, so landing
    // there announces which slide they moved to. preventScroll because the track's
    // own scrolling is what drives this component.
    if (heldFocus) this.slides[index].focus({ preventScroll: true });

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('hero-slideshow__dot--active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.syncTrackHeight();
  }

  /**
   * Sizes the track to the active slide, so moving to an image of a different shape
   * grows or shrinks the section. No-op outside adapt mode.
   *
   * The heights come from measure() rather than a read here, so this is a pure write.
   * They are the slides' own heights only because the CSS sets align-items: flex-start
   * on the track in adapt mode — without it every slide reports the tallest one's.
   */
  syncTrackHeight() {
    if (!this.adaptHeight) return;

    const height = this.slideHeights[this.index];
    if (!height) return;

    this.track.style.height = `${height}px`;
  }

  start() {
    this.isPlaying = true;
    this.resume();
    this.reflectPauseButton();
  }

  stop() {
    this.isPlaying = false;
    this.suspend();
    this.reflectPauseButton();
  }

  /**
   * resume/suspend move the timer without changing the visitor's play/pause intent,
   * so hovering the section does not flip the button's label.
   */
  resume() {
    this.suspend();
    this.track.setAttribute('aria-live', 'off');
    this.autoplayTimer = setInterval(() => this.step(1), this.autoplayDelay);
  }

  suspend() {
    if (this.autoplayTimer) clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
    if (this.track) this.track.setAttribute('aria-live', 'polite');
  }

  reflectPauseButton() {
    if (!this.pauseButton) return;

    this.pauseButton.classList.toggle('hero-slideshow__pause--paused', !this.isPlaying);

    const strings = window.accessibilityStrings || {};
    const label = this.isPlaying ? strings.pauseSlideshow : strings.playSlideshow;
    if (label) this.pauseButton.setAttribute('aria-label', label);
  }

  /**
   * Runs fn at most once per frame, on the frame itself. A timer-based throttle either
   * fires between frames — where the work is thrown away — or lags behind the scroll.
   */
  onFrame(fn) {
    let scheduled = false;
    return () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        // The theme editor can pull the section out between schedule and frame.
        if (this.isConnected) fn();
      });
    };
  }
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
